import React, {Component} from "react";
import * as ActionplanData from "./ActionplanData";
import * as d3 from "d3";
import { /* viewBys,*/ viewByLabels, categoryLabels, categoryStatus, categories} from "./Controls";

const barHeight = 25;

interface LabelProps {
    actionplanData: ActionplanData.ActionplanState,
    viewBy: string
}

export class ActionplanGraphLabels extends Component<LabelProps> {
    ref!: SVGElement | null;

    buildLabels() {
        const width = 150;
        const marginTop = 10;
        const marginBottom = 0;
        const marginLeft = 150;

        const viewByArray =this.props.actionplanData.sortedViewByCounts;
        const height = Math.ceil((viewByArray.length + 0.1) * barHeight) + marginTop + marginBottom;

        const y = d3.scaleBand()
            .domain(viewByArray.map(d => d.name))
            .rangeRound([marginTop, height - marginBottom])
            .padding(0.1);

        const svg = d3.select(this.ref)
            .attr("width", width)
            .attr("height", height)
            .attr("viewBox", [0, 0, width, height]);

        svg.selectAll("*").remove();

        svg.append("g")
            .attr("class", "axis")
            .attr("transform", `translate(${marginLeft},0)`)
            .call(d3.axisLeft(y).tickSizeOuter(0));

        const fullMap: {[key: number]: string} = {};

        svg.selectAll("text").each(function(this: SVGTextElement, d, i) {
            d3.select(this).attr("full-index", i);
            fullMap[i] = this.textContent || "";
            if (!this.textContent || (this.getComputedTextLength ? this.getComputedTextLength() : 140) <= 140) { return; }
            while (this.getComputedTextLength() > 110) {
                this.textContent = this.textContent.slice(0, -1);
            }
            this.textContent = `${this.textContent}...`;
        });

        svg.selectAll("text")
        .on("mouseover", function(event) {
                const datapoint = d3.select(".datapoint.d3Label")
                    .style("display", "block")
                    .style("left", (event.pageX + 50) + "px")
                    .style("top", event.pageY + "px");

                const full = fullMap[d3.select(event.target).attr("full-index")];
                datapoint.select("p").text(full);
            })
            .on("mousemove", (event) => {
                d3.select(".datapoint.d3Label")
                    .style("left", (event.pageX + 50) + "px")
                    .style("top", event.pageY + "px");
            })
            .on("mouseleave", () => {
                const datapoint = d3.select(".datapoint.d3Label");
                datapoint.style("display", "none");
            });
    }

    componentDidMount() { this.buildLabels(); }
    componentDidUpdate() { this.buildLabels(); }

    render() {
        return (
            <div className="col-auto d3LabelContainer">
                <div className={"datapoint d3Label"}>
                    <p></p>
                </div>
                <div className="d3ChartLabel">{ viewByLabels[this.props.viewBy]}</div>
                <div><svg className="d3Dashboard" ref={ref => this.ref = ref}></svg></div>
            </div>
        );
    }
}

interface ActionplanGraphProps {
    actionplanData: ActionplanData.ActionplanState,
    category: string // Grand total, Open and late, etc
    onSortClick?: (category: string, direction: number | undefined) => void
    onFocus?: (category: string, viewBy: string | undefined) => void
}

interface ActionplanGraphState {
    sort: number | undefined
    focusedViewBy: string | undefined;
}

export class ActionplanGraph extends Component<ActionplanGraphProps, ActionplanGraphState> {

    ref!: SVGElement | null;

    constructor(props, state) {
        super(props, state);
        this.state = {
            sort: undefined,
            focusedViewBy: undefined
        };
    }

    toggleFocus(viewBy: string) {
        const newFocusedViewBy = (viewBy === this.state.focusedViewBy) ? undefined : viewBy;
        this.setState({
            focusedViewBy: newFocusedViewBy
        }, () => {
            if (this.props.onFocus) {
                this.props.onFocus(this.props.category, newFocusedViewBy);
            }
        });
    }

    isFocused(viewBy: string) {
        if (this.props.actionplanData.focusedCategory && this.props.actionplanData.focusedViewBy) {
            return this.props.actionplanData.focusedCategory === this.props.category && this.props.actionplanData.focusedViewBy === viewBy;
        }
        return true;
    }

    buildGraph() {
        const marginTop = 10;
        const marginBottom = 0;

        const viewByArray = this.props.actionplanData.sortedViewByCounts;

        const height = Math.ceil((viewByArray.length + 0.1) * barHeight) + marginTop + marginBottom;

        const x = d3.scaleLinear()
            .domain([0, this.props.actionplanData.maxCount])
            .range([0, 80]); // Think of this as 0%-80% reserving 20% for text on the right

        const y = d3.scaleBand()
            .domain(viewByArray.map(d => d.name))
            .rangeRound([marginTop, height - marginBottom])
            .padding(0.1);

        const svg = d3.select(this.ref)
            .attr("width", "100%")
            .attr("height", height);

        svg.selectAll("*").remove();

        // Append a bar for each view by
        svg.append("g")
            .attr("class", "bars")
            .selectAll()
            .data(viewByArray)
            .join("rect")
            .attr("x", `${x(0)}%`)
            .attr("y", d => y(d.name) || 0)
            .attr("width", d => `${x(d[this.props.category])}%`)
            .attr("height", y.bandwidth())
            .attr("class", d => this.isFocused(d.name) ? "focused" : null)
            .on("mouseover", (event) => {
                const datapoint = d3.select(`.datapoint.${this.props.category}`)
                    .style("display", "block")
                    .style("left", (event.pageX + 50) + "px")
                    .style("top", event.pageY + "px");

                const data: ActionplanData.EntityCounts = (d3.select(event.target).datum()) as ActionplanData.EntityCounts;

                datapoint.select(".entity").text(data.name);
                const percent = Math.round(data[this.props.category] / data[categories.GRAND_TOTAL] * 100).toFixed(2);
                datapoint.select(".percent").text(percent + "%");
                datapoint.select(".number").text(data[this.props.category]);
            })
            .on("mousemove", (event) => {
                d3.select(`.datapoint.${this.props.category}`)
                    .style("left", (event.pageX + 50) + "px")
                    .style("top", event.pageY + "px");
            })
            .on("mouseleave", () => {
                const datapoint = d3.select(`.datapoint.${this.props.category}`);
                datapoint.style("display", "none");
            })
            .on("click", (event) => {
                const data = (d3.select(event.target).datum()) as ActionplanData.EntityCounts;
                this.toggleFocus(data.name);
            });

        svg.append("g")
            .attr("fill", "black")
            .attr("text-anchor", "start")
            .selectAll()
            .data(viewByArray.filter(d => d[this.props.category] !== 0))
            .join("text")
            .attr("x", d => `${x(d[this.props.category])}%`)
            .attr("y", d => (y(d.name) || 0) + y.bandwidth() / 2)
            .attr("dy", "0.35em")
            .attr("dx", "5")
            .text(d => d[this.props.category]);
    }

    componentDidMount() {
        this.buildGraph();
    }

    componentDidUpdate() {
        this.buildGraph();
    }

    handleSortClick = () => {
        let sort: number | undefined;

        if (this.state.sort === undefined) {
            sort = 1;
        } else if (this.state.sort === 1) {
            sort = 0;
        } else {
            sort = undefined;
        }

        // Sort goes from off (undefined), descending (0), ascending (1)
        this.setState({ sort: sort }, () => {
            // invoke the call back if provided.
            if (this.props.onSortClick) {
                this.props.onSortClick(this.props.category, this.state.sort);
            }
        });
    };

    render() {
        return (
            <div className="col d3GraphContainer">
                <div className={`datapoint ${this.props.category}`}>
                    <p>Status: <span className="category">{categoryStatus[this.props.category]}</span></p>
                    <p>For <em className={"entity"}></em></p>
                    <p>
                        % of Actionplans: <span className={"percent"}></span><br />
                        # of Actionplans: <span className={"number"}></span>
                    </p>
                </div>
                <div className="d3ChartLabel" onClick={this.handleSortClick}>{categoryLabels[this.props.category]}
                    &nbsp;
                    { (this.state.sort === undefined) ? null : <i className={`icon icon-angle-${this.state.sort === 1 ? "down" : "up"}`} />}
                </div>
                <div><svg className={`d3Dashboard ${this.props.category}`} ref={ref => this.ref = ref}></svg></div>
            </div>
        );
    }
}
